import { Button } from "antd";
import { styled, css } from "styled-components";
import { CustomButtonProps } from "./custom-button-type";
import { adjustColorOpacity } from "@/utils/rgba-hexadecimal/rgba-hexadecimal";
import { PaletteConfig } from "@/contexts/theme-context/config/palleteConfig";

const opacity = 0.7;

const CUSTOM_COLORS = {
  white: css`
    && {
      box-shadow: none;
      background: transparent;
      color: ${({ theme }) => theme?.colors?.text?.headings};
      border: 1px solid ${({ theme }) => theme?.colors?.text?.headings};
    }
    &&.ant-btn:hover {
      border: 1px solid ${({ theme }) => adjustColorOpacity(theme?.colors?.text?.headings, 0.2)};
      background: transparent;
      color: ${({ theme }) => adjustColorOpacity(theme?.colors?.text?.headings, opacity)};
    }
  `,
  dark: css`
    && {
      background: ${({ theme }) => theme?.colors?.text?.muted};
      color: ${({ theme }) => theme?.colors?.text?.headings};
    }
    &&.ant-btn:hover {
      background: ${({ theme }) => theme?.colors?.text?.neutral};
      color: ${({ theme }) => theme?.colors?.text?.headings};
    }
  `,
  gradient1: css`
    && {
      background: ${({ theme }) => theme?.colors?.gradient?.gradient1};
      color: ${({ theme }) => theme?.colors?.common?.white};
      border: none;
      box-shadow: none;
      outline: none;
    }
    &&.ant-btn:hover {
      background: ${({ theme }) => theme?.colors?.gradient?.gradient1};
      color: ${({ theme }) => theme?.colors?.common?.white};
      opacity: 0.7;
    }
  `
} as const;

const getStyles = (color: string, variant: CustomButtonProps['variant']) => {
  const STYLES = {
    solid: css`
      && {
        box-shadow: ${({ theme }) => theme?.shadows?.button};
        background: ${color};
        color: ${({ theme }) => theme?.colors?.common?.white};
        border: none;
      }
      &&.ant-btn:hover {
        background: ${adjustColorOpacity(color, opacity)};
        color: ${({ theme }) => theme.colors.common.white}
    `,
    outlined: css`
      && {
        border: 1px solid ${color};
        color: ${color};
        background: transparent;
      }
      &&.ant-btn.ant-btn-variant-outlined:hover {
        background: transparent;
        border: 1px solid ${adjustColorOpacity(color, opacity)};
        color: ${adjustColorOpacity(color, opacity)};
      }
    `,
    text: css`
      && {
        background: transparent;
        color: ${color};
        box-shadow: none;
        border: none;
      }
      &&.ant-btn.ant-btn-variant-text:hover {
        background: transparent;
        color: ${adjustColorOpacity(color, opacity)};
      }
    `,
    dashed: css`
      && {
        border: 1px dashed ${color};
        color: ${color};
        background: transparent;
      }
      &&.ant-btn.ant-btn-variant-dashed:hover {
        background: ${adjustColorOpacity(color, 0.2)};
        border-color: ${color};
        color: ${color};
      }
    `,
  } as const;

  return STYLES[variant ?? 'solid'];
};

const customizations = (
  theme: PaletteConfig,
  color: string,
  variant: CustomButtonProps['variant']
) => {
  const COLORS_THEME = {
    primary: theme.colors.theme.brandColor3,
    default: theme.colors.theme.brandColor3,
    success: theme.colors.theme.success,
    danger: theme.colors.theme.error
  };

  const resolvedColor = COLORS_THEME[color as keyof typeof COLORS_THEME] || theme.colors.theme.brandColor3;
  const resolvedCustomColor = CUSTOM_COLORS[color as keyof typeof CUSTOM_COLORS] || null;
  if (resolvedCustomColor) {
    return CUSTOM_COLORS[color as keyof typeof CUSTOM_COLORS];
  }
  return getStyles(resolvedColor, variant);
};

const ButtonStyled = styled(Button) <Pick<CustomButtonProps, "variant" | "color">>`
  display: flex;
  padding: ${({ theme }) => theme.sizes.padding.button};
  justify-content: center;
  align-items: center;
  min-height: 40px;
  gap: var(--Sizes---size-s, 8px);
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.sizes.borderRadius.button};


  &&.ant-btn[disabled] {
    opacity: 0.5;
    filter: grayscale(100%);
  }

  ${({ theme, color, variant }) =>
    customizations(theme as PaletteConfig, color ?? "primary", variant ?? "solid")};

  &&.ant-btn:active {
    color: ${({ theme }) => theme.colors.common.white}
    ${({ theme, color, variant }) =>
    customizations(theme as PaletteConfig, color ?? "primary", variant ?? "solid")};
  }
`;

export { ButtonStyled };
